import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BASE_URL from "../api/baseUrl";
import "./RecordsBoard.css";

// Landing hall of fame: Top teams + 6 record tabs (random matches only).
// One public, server-cached fetch. Each tab shows a small top-3 (top teams
// shows a few more). Loads async so it never blocks the first paint.

// Per-tab config: how to turn an entry into a middle "detail" line and a
// right-aligned "value". Team + coach (sub-line) is common to all.
const TABS = [
  { key: "top_teams",          top: 10, detail: () => null,
    value: () => null },
  { key: "fastest_win",        detail: (e) => `vs ${e.opponent}`,
    value: (e) => `${e.seconds}s` },
  { key: "earliest_goal",      detail: (e) => `${e.player} · vs ${e.opponent}`,
    value: (e) => `${e.seconds}s` },
  { key: "top_scorer_match",   detail: (e) => `${e.player} · vs ${e.opponent}`,
    value: (e) => `${e.goals} ⚽` },
  { key: "top_scorer_alltime", detail: (e) => e.player,
    value: (e) => `${e.goals} ⚽` },
  { key: "win_streak",         detail: () => null,
    value: (e) => `${e.streak} 🏆` },
  { key: "goal_diff",          detail: () => null,
    value: (e) => (e.diff > 0 ? `+${e.diff}` : `${e.diff}`) },
];

export function RecordsBoard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null); // null = loading
  const [active, setActive] = useState("top_teams");

  useEffect(() => {
    let cancelled = false;
    fetch(`${BASE_URL}/game/records`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setData(d || {}); })
      .catch(() => { if (!cancelled) setData({}); });
    return () => { cancelled = true; };
  }, []);

  const cfg = TABS.find((tb) => tb.key === active);
  const entries = data && Array.isArray(data[active])
    ? data[active].slice(0, cfg.top || 5)
    : (data ? [] : null);

  return (
    <div className="records-board">
      <h3 className="records-title">🏆 {t("records.title")}</h3>

      <div className="records-tabs-wrap">
        <div className="records-tabs" role="tablist">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              role="tab"
              className={`records-tab${active === tb.key ? " records-tab--active" : ""}`}
              onClick={(ev) => {
                setActive(tb.key);
                // keep the tapped tab in view (esp. mobile scroll bar)
                ev.currentTarget.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
              }}
            >
              {t(`records.tab.${tb.key}`)}
            </button>
          ))}
        </div>
      </div>

      <ol className="records-list">
        {entries === null ? (
          Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="records-row records-skeleton">
              <span className="records-team" />
            </li>
          ))
        ) : entries.length === 0 ? (
          <li className="records-empty">{t("records.empty")}</li>
        ) : (
          entries.map((e, i) => {
            const detail = cfg.detail(e);
            const value = cfg.value(e);
            return (
              <li key={i} className={`records-row${i === 0 ? " records-row--lead" : ""}`}>
                <span className="records-rank">{i + 1}</span>
                <span className="records-team">
                  {e.team}
                  {e.coach && <span className="coach-sub">{e.coach}</span>}
                </span>
                {detail && <span className="records-detail">{detail}</span>}
                {value && <span className="records-value">{value}</span>}
              </li>
            );
          })
        )}
      </ol>

      <p className="records-note">{t("records.note")}</p>
    </div>
  );
}
